import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Webhook, Save, Send, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import Layout from '../../components/Layout';
import { webhooksAPI } from '../../lib/api';


const WebhooksPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState({ slack: false, teams: false });
  const [config, setConfig] = useState({
    slack_webhook: '',
    slack_enabled: false,
    teams_webhook: '',
    teams_enabled: false
  });

  useEffect(() => {
    fetchConfig();
     
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await webhooksAPI.getConfig();
      
      if (response.data) {
        setConfig(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch webhook config:', error);
      // Don't show error if config doesn't exist yet
      if (error.response?.status !== 404) {
        if (error.response?.status !== 401) toast.error('Failed to load webhook configuration');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      await webhooksAPI.updateConfig(config);
      
      toast.success('Webhook configuration saved');
    } catch (error) {
      console.error('Failed to save config:', error);
      toast.error(error.response?.data?.detail || 'Failed to save configuration');
    }
  };

  const handleTest = async (type) => {
    const webhookUrl = type === 'slack' ? config.slack_webhook : config.teams_webhook;
    
    if (!webhookUrl) {
      toast.error(`Please enter a ${type} webhook URL first`);
      return;
    }
    
    setTesting({ ...testing, [type]: true });
    
    try {
      await webhooksAPI.test({ type, webhook_url: webhookUrl });
      
      toast.success(`Test message sent to ${type}!`, {
        description: 'Check your channel for the test notification'
      });
    } catch (error) {
      console.error('Test failed:', error);
      toast.error(`Test failed: ${error.response?.data?.detail || 'Could not send test message'}`);
    } finally {
      setTesting({ ...testing, [type]: false });
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-gray-500">Loading webhook configuration...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Webhook Notifications</h1>
        <p className="text-gray-600 mt-1">Configure notifications for Slack and Microsoft Teams</p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-900">
          <p className="font-medium mb-1">How to set up webhooks:</p>
          <ul className="list-disc list-inside space-y-1">
            <li><strong>Slack:</strong> Go to your Slack workspace → Apps → Incoming Webhooks → Add to Slack → Copy webhook URL</li>
            <li><strong>Teams:</strong> Open Teams channel → Connectors → Incoming Webhook → Configure → Copy webhook URL</li>
          </ul>
        </div>
      </div>

      {/* Slack Configuration */}
      <div className="bg-white rounded-lg shadow p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <svg className="w-7 h-7 text-purple-600" viewBox="0 0 24 24" fill="currentColor">
                <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z"/>
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-semibold">Slack</h2>
              <p className="text-sm text-gray-600">Send notifications to Slack channels</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-sm">Enabled</Label>
            <Switch
              checked={config.slack_enabled}
              onCheckedChange={(checked) => setConfig({...config, slack_enabled: checked})}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label>Webhook URL</Label>
          <Input
            placeholder="https://hooks.slack.com/services/..."
            value={config.slack_webhook}
            onChange={(e) => setConfig({...config, slack_webhook: e.target.value})}
          />
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => handleTest('slack')}
            disabled={testing.slack || !config.slack_webhook}
          >
            <Send className="w-4 h-4 mr-2" />
            {testing.slack ? 'Testing...' : 'Test Connection'}
          </Button>
          {config.slack_webhook && config.slack_enabled && (
            <div className="flex items-center gap-2 text-sm text-green-600">
              <CheckCircle2 className="w-4 h-4" />
              <span>Active</span>
            </div>
          )}
        </div>
      </div>

      {/* Microsoft Teams Configuration */}
      <div className="bg-white rounded-lg shadow p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <svg className="w-7 h-7 text-blue-600" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.625 8.127v7.746a1.127 1.127 0 01-1.127 1.127h-7.746a1.127 1.127 0 01-1.127-1.127V8.127A1.127 1.127 0 0111.752 7h7.746a1.127 1.127 0 011.127 1.127zM9.375 7H3.502A1.127 1.127 0 002.375 8.127v7.746A1.127 1.127 0 003.502 17h5.873a1.127 1.127 0 001.127-1.127V8.127A1.127 1.127 0 009.375 7zm12 10.873v1.625A2.502 2.502 0 0118.873 22H5.127A2.502 2.502 0 012.625 19.498v-1.625h18.75zm-3-15.748A2.252 2.252 0 0016.123 0h-.498a2.252 2.252 0 00-2.25 2.252v1.623h5.998V2.252z"/>
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-semibold">Microsoft Teams</h2>
              <p className="text-sm text-gray-600">Send notifications to Teams channels</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-sm">Enabled</Label>
            <Switch
              checked={config.teams_enabled}
              onCheckedChange={(checked) => setConfig({...config, teams_enabled: checked})}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label>Webhook URL</Label>
          <Input
            placeholder="https://outlook.office.com/webhook/..."
            value={config.teams_webhook}
            onChange={(e) => setConfig({...config, teams_webhook: e.target.value})}
          />
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => handleTest('teams')}
            disabled={testing.teams || !config.teams_webhook}
          >
            <Send className="w-4 h-4 mr-2" />
            {testing.teams ? 'Testing...' : 'Test Connection'}
          </Button>
          {config.teams_webhook && config.teams_enabled && (
            <div className="flex items-center gap-2 text-sm text-green-600">
              <CheckCircle2 className="w-4 h-4" />
              <span>Active</span>
            </div>
          )}
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} className="bg-amber-500 hover:bg-amber-600">
          <Save className="w-4 h-4 mr-2" />
          Save Configuration
        </Button>
      </div>

      {/* Info Box */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h3 className="font-semibold mb-2">Notification Events</h3>
        <p className="text-sm text-gray-600">Webhooks will be triggered for the following events:</p>
        <ul className="list-disc list-inside text-sm text-gray-600 mt-2 space-y-1">
          <li>New work order created</li>
          <li>Work order status changed</li>
          <li>Work order assigned to technician</li>
          <li>High priority work orders</li>
        </ul>
      </div>
    </div>
    </Layout>
  );
};

export default WebhooksPage;